/**
 * Import an existing project tracker from Excel or CSV (2026-09-28).
 *
 * 1. Pick a file → the server suggests which column is which and shows
 *    exactly what would be created (nothing is written yet).
 * 2. Adjust the mapping / row type / date order and preview again.
 * 3. Import → a single click undoes the whole import.
 *
 * Backend: backend/app/api/v1/endpoints/spreadsheet_import.py
 */
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { AlertTriangle, CheckCircle2, FileSpreadsheet, Loader2, Undo2, Upload } from 'lucide-react'

import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { spreadsheetImportService, type ImportPreview, type ImportResult, type ImportMode } from '../services/spreadsheetImport'
import { apiErrorMessage } from '../utils/apiError'

const FIELD_LABEL: Record<string, string> = {
  name: 'Project name', project: 'Project', title: 'Task title', description: 'Description',
  status: 'Status', priority: 'Priority', start_date: 'Start date', end_date: 'End date', due_date: 'Due date',
  budget: 'Budget', actual_cost: 'Spent to date', progress: '% complete', owner_email: 'Owner (email or name)',
  assignee_email: 'Assignee (email or name)', estimated_hours: 'Estimate (hours)',
}

export default function SpreadsheetImport() {
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [mapping, setMapping] = useState<Record<string, string | null>>({})
  const [mode, setMode] = useState<ImportMode | undefined>(undefined)
  const [dateOrder, setDateOrder] = useState<'dmy' | 'mdy' | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<ImportResult | null>(null)

  const runPreview = async (f: File, opts: { mode?: ImportMode; mapping?: Record<string, string | null>; dateOrder?: 'dmy' | 'mdy' } = {}) => {
    setBusy(true)
    try {
      const p = await spreadsheetImportService.preview(f, opts)
      setPreview(p)
      setMapping(p.mapping)
      setMode(p.mode)
      setDateOrder(p.date_order)
    } catch (err) {
      setPreview(null)
      toast.error(apiErrorMessage(err, 'Could not read that file.'))
    } finally {
      setBusy(false)
    }
  }

  const onPick = (f: File | undefined) => {
    if (!f) return
    setFile(f)
    setResult(null)
    runPreview(f)
  }

  const doImport = async () => {
    if (!file || !preview) return
    setBusy(true)
    try {
      const r = await spreadsheetImportService.apply(file, { mode, mapping, dateOrder })
      setResult(r)
      toast.success(`Imported ${r.projects_created} project(s) and ${r.tasks_created} task(s).`)
    } catch (err) {
      toast.error(apiErrorMessage(err, 'The import failed. Nothing was saved.'))
    } finally {
      setBusy(false)
    }
  }

  const undo = async () => {
    if (!result) return
    setBusy(true)
    try {
      const r = await spreadsheetImportService.undo(result.batch)
      toast.success(`Import undone: removed ${r.projects_removed} project(s) and ${r.tasks_removed} task(s).`)
      setResult(null)
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not undo the import.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Import from a spreadsheet</h1>
        <p className="text-sm text-muted-foreground">
          Bring in the tracker you already keep in Excel or Google Sheets (export it as .xlsx or .csv).
          You’ll see exactly what will be created before anything is saved, and one click undoes it.
        </p>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 pt-6">
          <input ref={inputRef} type="file" accept=".csv,.xlsx,.xlsm,text/csv" className="hidden"
            aria-label="Spreadsheet file" onChange={(e) => onPick(e.target.files?.[0])} />
          <Button onClick={() => inputRef.current?.click()} disabled={busy} className="min-h-[44px] gap-2">
            <Upload className="h-4 w-4" /> {file ? 'Choose another file' : 'Choose a file'}
          </Button>
          {file && (
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              <FileSpreadsheet className="h-4 w-4" /> {file.name}
              {preview ? ` · ${preview.row_count} rows` : ''}
            </span>
          )}
          {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label="Working" />}
        </CardContent>
      </Card>

      {result && (
        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6">
            <p className="flex items-center gap-2 text-sm text-foreground">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              Imported {result.projects_created} project(s) and {result.tasks_created} task(s).
              {result.unmatched_people.length > 0 &&
                ` ${result.unmatched_people.length} owner/assignee name(s) didn’t match anyone in your workspace, so you were set as the owner.`}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={undo} disabled={busy} className="min-h-[44px] gap-2">
                <Undo2 className="h-4 w-4" /> Undo import
              </Button>
              <Button onClick={() => navigate('/project')} className="min-h-[44px]">View projects</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {preview && !result && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>How to read the file</CardTitle>
              <CardDescription>We guessed these from your column names. Change anything that’s wrong.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-4 text-sm">
                <label className="flex items-center gap-2">
                  Each row is a
                  <select value={mode} disabled={busy} aria-label="Row type"
                    onChange={(e) => { const m = e.target.value as ImportMode; if (file) runPreview(file, { mode: m, dateOrder }) }}
                    className="min-h-[44px] rounded-md border border-border bg-background px-2">
                    <option value="projects">project</option>
                    <option value="tasks">task (with a project column)</option>
                  </select>
                </label>
                <label className="flex items-center gap-2">
                  Dates like 03/04/2026 mean
                  <select value={dateOrder} disabled={busy} aria-label="Date order"
                    onChange={(e) => { const d = e.target.value as 'dmy' | 'mdy'; setDateOrder(d); if (file) runPreview(file, { mode, mapping, dateOrder: d }) }}
                    className="min-h-[44px] rounded-md border border-border bg-background px-2">
                    <option value="dmy">3 April (day first)</option>
                    <option value="mdy">March 4 (month first)</option>
                  </select>
                </label>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {preview.fields.map((f) => (
                  <label key={f} className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-foreground">
                      {FIELD_LABEL[f] ?? f}{preview.required.includes(f) ? ' *' : ''}
                    </span>
                    <select value={mapping[f] ?? ''} disabled={busy} aria-label={FIELD_LABEL[f] ?? f}
                      onChange={(e) => setMapping((m) => ({ ...m, [f]: e.target.value || null }))}
                      className="min-h-[44px] w-56 rounded-md border border-border bg-background px-2">
                      <option value="">Not in my file</option>
                      {preview.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </label>
                ))}
              </div>
              <Button variant="outline" disabled={busy || !file} className="min-h-[44px]"
                onClick={() => file && runPreview(file, { mode, mapping, dateOrder })}>
                Update preview
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>What will be created</CardTitle>
              <CardDescription>
                {preview.projects} new project(s){preview.mode === 'tasks' ? ` and ${preview.tasks} task(s)` : ''}.
                {preview.error_count > 0 && ` ${preview.error_count} row(s) will be skipped.`}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {(preview.errors.length > 0 || preview.warnings.length > 0) && (
                <ul className="max-h-56 space-y-1 overflow-y-auto text-sm" aria-label="Problems found">
                  {preview.errors.map((e) => (
                    <li key={`e${e.row}${e.message}`} className="flex gap-2 text-red-700">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0" /> Row {e.row}: {e.message}
                    </li>
                  ))}
                  {preview.warnings.map((w) => (
                    <li key={`w${w.row}${w.message}`} className="flex gap-2 text-muted-foreground">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0" /> Row {w.row}: {w.message}
                    </li>
                  ))}
                </ul>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <caption className="sr-only">First rows of your file</caption>
                  <thead>
                    <tr>{preview.headers.map((h) => <th key={h} className="border-b border-border px-2 py-1 font-medium">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {preview.sample.map((row, i) => (
                      <tr key={i}>{preview.headers.map((h) => <td key={h} className="border-b border-border px-2 py-1 text-muted-foreground">{row[h]}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button onClick={doImport} disabled={busy || (preview.projects === 0 && preview.tasks === 0)} className="min-h-[44px]">
                Import {preview.mode === 'tasks' ? `${preview.tasks} task(s)` : `${preview.projects} project(s)`}
              </Button>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
